import { EventBus } from "jiffy-store";
import events from "common/events";
import "./delivery-date-pill.scss";

const LOADING_TEXT = "Getting earliest delivery date";

class DeliveryDatePill {
  constructor($element) {
    this.$element = $element;
    this.#init();
  }

  get $textElement() {
    return this.$element.querySelector(".js-delivery-date-pill-text");
  }

  get $prefixElement() {
    return this.$element.querySelector(".js-delivery-date-pill-prefix");
  }

  /**
   * Checks if the current day is a weekend
   * @returns {boolean} True if the current day is a weekend, false otherwise
   */
  static #isWeekend() {
    const now = new Date();
    const dayOfWeek = now.getDay();

    return dayOfWeek === 0 || dayOfWeek === 6;
  }

  /**
   * Checks if delivery date is today
   * @param {string} deliveryDate - The delivery date string
   * @returns {boolean}
   */
  static #isSameDay(deliveryDate) {
    if (!deliveryDate) return false;

    return deliveryDate.toLowerCase().includes("today");
  }

  /**
   * Checks if delivery date is tomorrow
   * @param {string} deliveryDate - The delivery date string
   * @returns {boolean}
   */
  static #isNextDay(deliveryDate) {
    if (!deliveryDate) return false;

    return deliveryDate.toLowerCase().includes("tomorrow");
  }

  #init() {
    EventBus.on(events.DELIVERY_DATE_LOADING_START, this.#showLoadingState.bind(this));
    EventBus.on(events.DELIVERY_DATE_UPDATED, this.#updateDeliveryDate.bind(this));

    this.$element.classList.remove("delivery-date-pill--skeleton");
    this.#showLoadingState();
    this.#show();
  }

  #show() {
    this.$element.classList.remove("hidden");
  }

  #hide() {
    this.$element.classList.add("hidden");
  }

  #showLoadingState() {
    this.$prefixElement.classList.add("hidden");
    this.$textElement.textContent = LOADING_TEXT;
    this.#show();
  }

  #updateDeliveryDate(evt) {
    const deliveryData = evt.target;
    if (
      !deliveryData ||
      !deliveryData.composition_dates ||
      !deliveryData.composition_dates.value_full
    ) {
      this.#hide();
      return;
    }

    const deliveryDate = deliveryData.composition_dates.value_full;
    const isWeekend = DeliveryDatePill.#isWeekend();
    const isSameDay = DeliveryDatePill.#isSameDay(deliveryDate);
    const isNextDay = DeliveryDatePill.#isNextDay(deliveryDate);

    let prefix;
    let text;

    if (isWeekend || (!isSameDay && !isNextDay)) {
      prefix = "Get it by";
      text = deliveryDate;
    } else if (isSameDay) {
      prefix = "Get it";
      text = "Today";
    } else if (isNextDay) {
      prefix = "Get it";
      text = "Tomorrow";
    }

    this.$prefixElement.textContent = prefix;
    this.$prefixElement.classList.remove("hidden");
    this.$textElement.textContent = text;
    this.#show();
  }

  destroy() {
    EventBus.off(events.DELIVERY_DATE_LOADING_START, this.#showLoadingState.bind(this));
    EventBus.off(events.DELIVERY_DATE_UPDATED, this.#updateDeliveryDate.bind(this));
  }
}

const instances = new Map();

const $getPills = () => document.querySelectorAll(".js-delivery-date-pill");

const initDeliveryDatePills = () => {
  $getPills().forEach($pill => {
    if (!instances.has($pill)) {
      const instance = new DeliveryDatePill($pill);
      instances.set($pill, instance);
    }
  });
};

export { DeliveryDatePill };
export default initDeliveryDatePills;
